import { BaseWebComponentController } from './BaseWebComponentController';

interface IInitialSetup {
  controllerConstructor: typeof BaseWebComponentController;
}

export class BaseWebComponentElement extends HTMLElement {
  public started: boolean;
  public dependenciesInjected: boolean;
  public dependencies: any;
  public controllerConstructor: typeof BaseWebComponentController;
  public controller: BaseWebComponentController | null;

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ['data-properties-set'];
  }

  get propertiesSet(): boolean {
    return this.hasAttribute('data-properties-set');
  }

  set propertiesSet(value: boolean) {
    if (value) {
      this.setAttribute('data-properties-set', 'true');
    } else {
      this.removeAttribute('data-properties-set');
    }
  }

  initialSetup({ controllerConstructor }: IInitialSetup) {
    this.started = false;

    this.dependenciesInjected = false;
    this.dependencies = {};

    this.controllerConstructor = controllerConstructor;
    this.controller = null;
  }

  injectDependencies(dependencies: any) {
    if (this.dependenciesInjected) {
      return;
    }
    this.dependencies = dependencies;
    this.build();
    this.dependenciesInjected = true;
  }

  build() {
    this.buildController();
  }

  buildController() {
    if (!this.controllerConstructor) {
      throw new Error('View constructor not defined');
    }
    this.controller = new this.controllerConstructor(this);
    if (this.controller) {
      this.controller.injectDependencies(this.dependencies);
    }
  }

  connectedCallback() {
    if (this.controller) {
      this.controller.connected();
    }
  }

  disconnectedCallback() {
    if (this.controller) {
      this.controller.disconnected();
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (this.propertiesSet && this.controller) {
      this.controller.render().then().catch();
    }
  }

  async start() {
    if (!this.dependenciesInjected) {
      return;
    }
    if (this.started) {
      return;
    }
    if (!this.controller) {
      return;
    }
    this.started = true;
    await this.controller.start();
  }

  async stop() {
    if (!this.started) {
      return;
    }
    if (!this.controller) {
      return;
    }
    await this.controller.stop();
    this.started = false;
  }

  protected getArrayValueFromAttribute(
    component: BaseWebComponentElement,
    attribute: string,
    key: string,
    defaultValue:any = []
  ) {
    try {
      const value = component.getAttribute(attribute);
      if (!value) {
        return defaultValue;
      }
      return JSON.parse(value)[key] || defaultValue;
    } catch (e) {
      return defaultValue;
    }
  }

  protected getValueFromAttribute(component: BaseWebComponentElement, attribute: string, defaultValue?: any) {
    try {
      const value = component.getAttribute(attribute);
      if (!value) {
        return defaultValue || undefined;
      }
      return JSON.parse(value);
    } catch (e) {
      return defaultValue || undefined;
    }
  }

  public setViewLoadingState(): void {
    if (!this.controller || !this.controller.view) {
      return;
    }
    this.controller.view.setLoadingState();
  }

  public removeViewLoadingState(): void {
    if (!this.controller || !this.controller.view) {
      return;
    }
    this.controller.view.removeLoadingState();
  }
}
